import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Gift, ShieldCheck } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { usePageMeta } from "@/hooks/usePageMeta";
import { SITE } from "@/data/site";
import { ALL_LOCATIONS } from "@/data/bookingOptions";
import { trackEvent, attachCampaign } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blankForm = {
  referring_parent_name: "",
  referring_child_name: "",
  new_parent_name: "",
  new_child_name: "",
  email: "",
  phone: "",
  preferred_class: "",
  preferred_location: "",
  notes: "",
  consent: false,
};

const Field = ({ label, children, testId }) => (
  <label data-testid={testId} className="block">
    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 font-bold">{label}</div>
    {children}
  </label>
);

export default function KidsReferralPage() {
  usePageMeta(
    "Parent Referral — Invite a Family",
    "Invite another parent to try a USDD Kids class. Referred families book the £5 Kids Taster — and you get a thank-you credit once their child attends."
  );
  const [form, setForm] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.referring_parent_name || !form.new_parent_name || !form.email || !form.phone) {
      toast.error("Please fill referring parent, new parent, email and phone.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm consent to be contacted.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/enquiries/referral`, attachCampaign(form));
      trackEvent("referral_submit", { kind: "kids_parent_referral" });
      setDone(true);
      toast.success("Referral submitted — we'll be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit referral. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main>
        <PageHero
          overline="USDD Kids · Family Referral"
          title="Invite a Parent."
          highlight="£5 Taster Referral."
          description="Existing USDD parents — invite another family to try a USDD Kids class. Referred families book the existing £5 Kids Taster with your name or referral code. We'll thank you with a credit once the referred child attends."
          ctaPrimary={{ label: "Send a Referral", to: "#referral-form" }}
          ctaSecondary={{ label: "USDD Kids", to: "/kids" }}
          bg={SITE.kidsShow}
        />

        <section id="referral-form" className="relative py-16 sm:py-20 bg-black overflow-hidden">
          <div className="absolute -top-20 right-1/4 w-[440px] h-[440px] rounded-full bg-brand-yellow/10 blur-[120px] pointer-events-none" />
          <div className="absolute -bottom-20 left-1/4 w-[360px] h-[360px] rounded-full bg-brand-purpleLight/15 blur-[110px] pointer-events-none" />

          <div className="relative max-w-3xl mx-auto px-6 sm:px-8">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl border border-brand-yellow/40 bg-gradient-to-br from-brand-purpleDeep/40 via-zinc-950 to-black p-10 text-center"
                data-testid="referral-thanks"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-yellow/15 border border-brand-yellow/40 mb-5">
                  <Gift size={22} className="text-brand-yellow" />
                </div>
                <h3 className="font-display text-3xl sm:text-4xl text-white tracking-wide">Referral submitted.</h3>
                <p className="mt-3 text-sm text-zinc-300 leading-relaxed max-w-xl mx-auto">
                  Thanks — we'll be in touch with the referred family shortly to book their £5 Kids Taster. Once the child attends, we'll credit your account as a thank-you.
                </p>
                <Link
                  to="/kids"
                  className="inline-flex items-center gap-2 mt-7 px-6 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-xs tracking-[0.2em] hover:bg-brand-yellowHover hover:scale-[1.02] transition-all glow-yellow"
                >
                  Back to USDD Kids <ArrowRight size={14} />
                </Link>
              </motion.div>
            ) : (
              <motion.form
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55 }}
                onSubmit={onSubmit}
                data-testid="referral-form"
                className="relative overflow-hidden rounded-3xl border border-brand-yellow/30 bg-gradient-to-br from-brand-purpleDeep/30 via-zinc-950 to-black p-7 sm:p-10 space-y-5"
              >
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/70 to-transparent" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.32em] text-brand-yellow font-black mb-2">Step 1 of 1 · Referral form</div>
                  <h2 className="font-display text-3xl sm:text-4xl text-white tracking-wide leading-[1]">
                    Refer a Family · £5 Taster.
                  </h2>
                  <p className="mt-3 text-sm text-zinc-400">
                    The referring parent shares their name. The new family books the £5 Kids Taster with that name — and we credit your account once their child attends.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Referring parent name *" testId="ref-rp-name">
                    <input type="text" required value={form.referring_parent_name} onChange={update("referring_parent_name")} className="usdd-input" placeholder="e.g. Sam Johnson" data-testid="ref-rp-name-input" />
                  </Field>
                  <Field label="Referring child name" testId="ref-rc-name">
                    <input type="text" value={form.referring_child_name} onChange={update("referring_child_name")} className="usdd-input" placeholder="e.g. Maya" data-testid="ref-rc-name-input" />
                  </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="New parent name *" testId="ref-np-name">
                    <input type="text" required value={form.new_parent_name} onChange={update("new_parent_name")} className="usdd-input" placeholder="e.g. Aisha Khan" data-testid="ref-np-name-input" />
                  </Field>
                  <Field label="New child name" testId="ref-nc-name">
                    <input type="text" value={form.new_child_name} onChange={update("new_child_name")} className="usdd-input" placeholder="e.g. Yusuf" data-testid="ref-nc-name-input" />
                  </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Email *" testId="ref-email">
                    <input type="email" required value={form.email} onChange={update("email")} className="usdd-input" placeholder="parent@example.com" data-testid="ref-email-input" />
                  </Field>
                  <Field label="Phone / WhatsApp *" testId="ref-phone">
                    <input type="tel" required value={form.phone} onChange={update("phone")} className="usdd-input" placeholder="07XXX XXXXXX" data-testid="ref-phone-input" />
                  </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Preferred class" testId="ref-class">
                    <input type="text" value={form.preferred_class} onChange={update("preferred_class")} className="usdd-input" placeholder="e.g. Kids · Mondays 5pm" data-testid="ref-class-input" />
                  </Field>
                  <Field label="Preferred location" testId="ref-location">
                    <select value={form.preferred_location} onChange={update("preferred_location")} className="usdd-input cursor-pointer" data-testid="ref-location-input">
                      <option value="">Any / not sure</option>
                      {ALL_LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </Field>
                </div>

                <Field label="Anything else?" testId="ref-notes">
                  <textarea rows={3} value={form.notes} onChange={update("notes")} className="usdd-input resize-none" placeholder="Other context — age, friends already at USDD, availability…" data-testid="ref-notes-input" />
                </Field>

                <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="ref-consent">
                  <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 h-4 w-4 rounded border-white/30 bg-white/10 text-brand-yellow focus:ring-brand-yellow" data-testid="ref-consent-input" />
                  <span className="text-xs text-zinc-300 leading-relaxed">
                    <span className="font-bold text-white">I consent to be contacted</span> about this referral and the resulting £5 Kids Taster booking. See our Privacy &amp; GDPR Policy.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="referral-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover hover:scale-[1.02] transition-all glow-yellow disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                  Send Referral <ArrowRight size={16} />
                </button>

                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5 justify-center">
                  <ShieldCheck size={12} /> Your details stay private — info@usddnetworks.co.uk
                </div>
              </motion.form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
